/**
 * agentTree — turn the reducer's flat run-level roster (``agentRoster``) into a nested tree for
 * the room's hierarchy panel, plus a flattened pre-order walk carrying the guide-line metadata a
 * renderer needs to draw ├─ / └─ / │ connectors.
 *
 * The roster is a flat array of {agentId, parentAgentId, level, ...} rows accreted from AppSync
 * events (see agentRoomReducer). This module is pure + defensive so it can be unit-tested without
 * React: bad/cyclic/orphan parents degrade to roots rather than throwing.
 */

/**
 * Merge the STABLE seeded roster (full topology from the manifest, all ``queued``) with the LIVE
 * reducer roster (status/text/reasoning/builder-stream accreted from AppSync). Keyed by agentId.
 *
 * This is what makes the panel STABLE: the seeded rows define the row SET + ORDER up front (so the
 * list never jumps as distributed agents report in); live rows only overlay mutable fields
 * (status, text, usage, reasoning, builderLines, builderCompletion, runId) onto the matching row.
 *
 * ``selected`` (did the Lead engage this agent for THIS run?) is derived: an agent that produced
 * ANY live signal (status advanced past ``queued``) was selected; one still ``queued`` was NOT
 * selected (the Lead routed around it). Non-selected rows render grayed-out; clicking one shows the
 * "not required" note + the Lead's routing reasoning. When ``runSettled`` is true (run finished),
 * a still-``queued`` row is definitively ``not-selected``; while the run is live it's just pending.
 *
 * A live row with no seeded match (topology drift / dynamic node) is appended (never dropped) so we
 * never lose a real agent — falls back to the old event-accreted behavior for that row.
 *
 * @param {Array<Object>} seeded  from manifestToRoster (stable topology)
 * @param {Array<Object>} live    reducer ``agentRoster``
 * @param {{runSettled?: boolean}} [opts]
 * @returns {Array<Object>} merged rows (seeded order preserved; drift appended), each with
 *   ``selected: boolean`` and ``notSelected: boolean`` (only true once the run has settled).
 */
export function mergeRoster(seeded, live, opts = {}) {
  const runSettled = !!opts.runSettled;
  // Manual stage cap (frozen release setting): stage-depth (row.level, 1-based) > cap → the agent
  // is part of the org but DISABLED this release (the user limited run depth). 0/absent → no cap.
  // Distinct from ``notSelected`` (a per-RUN Lead routing skip): ``cappedOut`` is a deliberate
  // CONFIG state the whole release carries, so the row reads "disabled this release", not "not
  // required for this task".
  const stageCap = Math.max(0, Number.parseInt(opts.stageDepthCap, 10) || 0);
  const seededArr = Array.isArray(seeded) ? seeded.filter((r) => r && r.agentId) : [];
  const liveArr = Array.isArray(live) ? live.filter((r) => r && r.agentId) : [];
  const liveById = new Map(liveArr.map((r) => [String(r.agentId), r]));
  const usedLive = new Set();

  const decorate = (row) => {
    const advanced = row.status && row.status !== 'queued';
    const lvl = Number(row.level);
    const cappedOut = stageCap > 0 && Number.isFinite(lvl) && lvl > stageCap;
    // Engine-stage rows (Lead/Reducer lines) are orchestration work, not routable agents — they're
    // never "not required" (they always run when their node runs) and never capped-out as an agent.
    if (row.isEngineStage) {
      return { ...row, selected: !!advanced, notSelected: false, cappedOut: false };
    }
    return {
      ...row,
      selected: !!advanced,
      // "Not required" is only asserted once the run is over — mid-run a queued agent may still run.
      notSelected: runSettled && !advanced,
      cappedOut,
    };
  };

  const merged = seededArr.map((s) => {
    const lv = liveById.get(String(s.agentId));
    if (!lv) return decorate(s);
    usedLive.add(String(s.agentId));
    // Live overlays mutable fields onto the stable seeded row; seeded keeps identity/order/topology.
    return decorate({
      ...s,
      ...lv,
      // Prefer the seeded topology (stable) for tree placement; live may lack level/parent early.
      level: s.level ?? lv.level,
      parentAgentId: s.parentAgentId ?? lv.parentAgentId,
      nodePath: s.nodePath || lv.nodePath,
      label: lv.label || s.label,
    });
  });

  // Drift: any live agent not in the seeded topology → append (never lose a real agent).
  for (const lv of liveArr) {
    if (!usedLive.has(String(lv.agentId))) merged.push(decorate(lv));
  }
  return merged;
}

/**
 * Build a parent→children tree from a flat roster.
 *
 * A row is a ROOT when its ``parentAgentId`` is absent/blank OR points at an id that isn't in the
 * roster (an orphan — e.g. the parent's event hasn't arrived yet, or we're mid-run). Children keep
 * the roster's given order (which the reducer already sorts by level then first-seen).
 *
 * @param {Array<Object>} roster
 * @returns {Array<{row: Object, children: Array}>}  root nodes, each with nested ``children``
 */
export function buildAgentTree(roster) {
  const rows = Array.isArray(roster) ? roster.filter((r) => r && r.agentId) : [];
  const byId = new Map(rows.map((r) => [String(r.agentId), r]));

  // Raw parent→child adjacency (may contain a cycle if the wire ever sent A↔B). We build the tree
  // nodes DURING an acyclic DFS below so a bad cycle can never produce a self-referential
  // ``children`` structure (which would make flatten/render infinite-loop).
  const childIds = new Map(rows.map((r) => [String(r.agentId), []]));
  const rootIds = [];
  for (const r of rows) {
    const id = String(r.agentId);
    const pid = (typeof r.parentAgentId === 'string' && r.parentAgentId.trim())
      ? r.parentAgentId.trim() : '';
    // Root when no parent, parent missing from roster (orphan — parent event not yet arrived), or
    // a self-loop.
    if (!pid || pid === id || !byId.has(pid)) rootIds.push(id);
    else childIds.get(pid).push(id);
  }

  // NEST specialists UNDER their Group Lead (display transform, data untouched). A group's router
  // ("Group Lead", ``__lead__{nodePath}``) DISPATCHED its specialists, so the tree reads better with
  // them one layer deeper — under the Lead — rather than as its flat siblings:
  //     Fundamentals Lead              Fundamentals Lead
  //     ├─ Group Lead          →       ├─ Group Lead
  //     ├─ Valuation Spec              │    ├─ Valuation Spec
  //     ├─ Earnings Spec               │    └─ Earnings Spec
  //     └─ Summarizer                  └─ Summarizer
  // The Summarizer (reducer) STAYS a sibling of the Group Lead — it folds the group AFTER the
  // specialists, a phase-peer of the router, not one of the routed workers. Only moves real
  // specialists (non-stage rows); groups with no engine-lead row are untouched (non-generative).
  for (const [pid, kids] of childIds) {
    const leadId = kids.find((cid) => {
      const r = byId.get(cid);
      return r && r.isEngineStage && r.stageKind !== 'reducer';
    });
    if (!leadId) continue;
    const specialists = kids.filter((cid) => {
      if (cid === leadId) return false;
      const r = byId.get(cid);
      return !(r && r.isEngineStage);   // keep stage rows (Lead + Summarizer) at the group level
    });
    if (!specialists.length) continue;
    const specialistSet = new Set(specialists);
    childIds.set(pid, kids.filter((cid) => !specialistSet.has(cid)));  // Lead + Summarizer remain
    childIds.get(leadId).push(...specialists);                          // specialists → under Lead
    void pid;
  }

  // SEMANTIC sibling order (not event-timing): within a group the LEAD (router) reads FIRST, the
  // specialists in the middle, and the SUMMARIZER (reducer) LAST — that's the lifecycle the user
  // expects (route → work → fold). Event ``seq`` alone drifted the Lead down next to the Summarizer
  // when its row happened to be created after the specialist finals (live/replay ordering). So we
  // rank engine-stage rows explicitly: lead = top (-1), reducer = bottom (+1), everyone else keeps
  // their relative (seq) order in the middle (0). Stable sort preserves the specialists' order.
  const siblingRank = (id) => {
    const r = byId.get(id);
    if (r && r.isEngineStage) return r.stageKind === 'reducer' ? 1 : -1;
    return 0;
  };
  for (const list of childIds.values()) {
    list.sort((a, b) => siblingRank(a) - siblingRank(b));   // Array.sort is stable → middle keeps seq
  }
  // Same rank at the ROOT level: the "Network Lead" (``__lead__root``) reads ABOVE the group-lead
  // nodes, and "Network Summarizer" (``__reducer__root``) BELOW them — so the whole run reads
  // route → groups → fold, top to bottom. rootIds are level-1 siblings just like a group's children.
  rootIds.sort((a, b) => siblingRank(a) - siblingRank(b));

  const visited = new Set();
  const attach = (id) => {
    visited.add(id);
    const node = { row: byId.get(id), children: [] };
    for (const cid of childIds.get(id)) {
      if (!visited.has(cid)) node.children.push(attach(cid));  // back-edge (cycle) → skip
    }
    return node;
  };

  const roots = rootIds.map(attach);
  // Any node still unvisited belongs to a cycle-only component (no acyclic path from a root) —
  // promote it to a root so it's still rendered rather than silently dropped.
  for (const r of rows) {
    const id = String(r.agentId);
    if (!visited.has(id)) roots.push(attach(id));
  }

  return roots;
}

/**
 * Pre-order flatten of the tree into render rows carrying guide-line metadata.
 *
 * Each emitted row: { row, depth, isLast, ancestorsLast }.
 *   - ``depth``          0-based nesting depth (roots = 0), for indentation.
 *   - ``isLast``         true when this node is its parent's LAST child → draws └─ (else ├─).
 *   - ``ancestorsLast``  boolean[] (one per ancestor level) — true where that ancestor was a last
 *                        child, so the renderer knows whether to draw a vertical │ guide or a gap.
 *
 * @param {Array<Object>} roster
 * @returns {Array<{row: Object, depth: number, isLast: boolean, ancestorsLast: boolean[]}>}
 */
export function flattenAgentTree(roster) {
  const roots = buildAgentTree(roster);
  const out = [];
  const walk = (node, depth, isLast, ancestorsLast) => {
    out.push({ row: node.row, depth, isLast, ancestorsLast });
    const kids = node.children;
    kids.forEach((child, i) => {
      walk(child, depth + 1, i === kids.length - 1, [...ancestorsLast, isLast]);
    });
  };
  roots.forEach((root, i) => walk(root, 0, i === roots.length - 1, []));
  return out;
}
